import { i, s, x, A } from '../../lit-element.js';
import '../../fs-heading.js';
import { completeAnimation } from './assets/completeAnimation.js';
import { curtainAnimation } from './assets/curtainAnimation.js';
import '../../query-assigned-elements.js';
import '../../index2.js';

const style = i`
  :host {
    position: fixed;
    z-index: 999999;
  }

  [hidden],
  .hidden {
    display: none;
  }

  #animation-slot,
  #animation-text {
    align-items: center;
    color: white;
    display: flex;
    font-family: Optimist, 'Helvetica Neue', 'Helvetica', 'Arial', sans-serif;
    font-size: 1.5rem;
    font-weight: 600;
    justify-content: center;
    margin: 1rem;
    text-align: center;
  }

  #content {
    left: 50%;
    position: fixed;
    top: 50%;
    transform: translate(-50%, -50%);
    z-index: 100000000;
  }

  .refi-curtain,
  .cover {
    height: 100vh;
    left: 0;
    pointer-events: none;
    position: fixed;
    right: 0;
    top: 0;
    width: 100vw;
  }

  .cover {
    background: white;
  }

  svg#curtain {
    height: 100vh;
    width: 100vw;
  }

  #modal {
    background: var(--refi-header-color);
    border: var(--refi-header-color);
  }
`;

class RefiTransitionPage extends s {
  constructor() {
    super();
    this.loading = true;
    this.showCompleteAnimation = false;
    this.transitionComplete = false;
    this.initiateAnimationCompletion = false;
    this.animationTimeout = null;
    this.minimumAnimationLengthComplete = false;
    this.reverseTransitionComplete = false;
    this.transitionTimeout = null;
    this.completionAnimationTimeout = null;
    this.successToggleComplete = false;
    this.successToggleTimeout = null;
    this.skipCurtainOpenAnimation = false;
    this.skipCurtainCloseAnimation = false;
  }

  static styles = [style];

  static properties = {
    loading: { type: Boolean, reflect: true },
    showCompleteAnimation: { type: Boolean, attribute: 'show-completion-animation', reflect: true },
    skipCurtainCloseAnimation: { type: Boolean, attribute: 'skip-curtain-close-animation', reflect: true },
    skipCurtainOpenAnimation: { type: Boolean, attribute: 'skip-curtain-open-animation', reflect: true },
    initiateAnimationCompletion: { type: Boolean, attribute: 'initiate-animation-completion', reflect: true },
    transitionComplete: { type: Boolean, reflect: true },
    reverseTransitionComplete: { type: Boolean, reflect: true },
    minimumAnimationLengthComplete: { type: Boolean },
    minimumAnimationLengthMs: { type: Number, attribute: 'minimum-animation-length-ms' },
    successToggleComplete: { type: Boolean },
    successToggleLengthMs: { type: Number, attribute: 'success-animation-length-ms' },
  };

  updated(changes) {
    super.updated(changes);

    if (changes.get('skipCurtainOpenAnimation') !== undefined && changes.get('skipCurtainOpenAnimation') !== this.skipCurtainOpenAnimation) {
      this.animationTimeout = null;
      this.completionAnimationTimeout = null;
      this.successToggleTimeout = null;
      this.transitionTimeout = null;
      this.initiateAnimationCompletion = false;
      this.minimumAnimationLengthComplete = false;
      this.reverseTransitionComplete = false;
      this.showCompleteAnimation = false;
      this.successToggleComplete = false;
      this.transitionComplete = false;
    }
    if (changes.has('successToggleLengthMs') && changes.get('successToggleLengthMs') !== this.successToggleLengthMs) {
      this.resetAnimationTimeout();
    }
    if (changes.has('initiateAnimationCompletion') && changes.get('initiateAnimationCompletion') !== this.initiateAnimationCompletion) {
      if (this.initiateAnimationCompletion) {
        this.successToggleTimeout = null;
      }
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this.resetAnimationTimeout();
  }

  render() {
    return x`${this.renderAnimation()}`;
  }

  renderAnimation() {
    // if everything is complete, begin the reverse curtain if not skipped
    if (this.transitionComplete && this.transitionTimeout === null) {
      if (this.skipCurtainCloseAnimation) {
        // Keep the success checkmark going
        this.dispatchEvent(new CustomEvent('transition-complete', {}));
        this.transitionComplete = false;
        this.successToggleComplete = true;
        this.resetAnimationTimeout();
      } else {
        // else begin reverse animation
        this.loading = true;
        const curtainElement = this.shadowRoot.getElementById('curtainAnimateReverse');
        curtainElement.beginElement();
        this.transitionTimeout = setTimeout(() => {
          this.dispatchEvent(new CustomEvent('transition-complete', {}));
          this.reverseTransitionComplete = true;
        }, 1000);
      }
    }
    // if the animation is complete and the host has also initiated completion, show 'success' animation state
    if (this.initiateAnimationCompletion && this.minimumAnimationLengthComplete && this.successToggleTimeout === null) {
      const animations = this.querySelectorAll('.animate');
      animations.forEach(animation => {
        animation.classList.toggle('success');
      });
      this.successToggleTimeout = setTimeout(() => {
        this.successToggleComplete = true;
      }, this.successToggleLengthMs ?? 1000);
    }
    // if the animation is complete and the host has also initiated completion and success class has been toggled, begin success checkmark animation
    const beginCompletionAnimation = this.initiateAnimationCompletion && this.minimumAnimationLengthComplete && this.successToggleComplete;
    if (beginCompletionAnimation && this.completionAnimationTimeout === null) {
      setTimeout(() => {
        this.dispatchEvent(new CustomEvent('curtain-close-initiated', {}));
      }, 800);
      this.completionAnimationTimeout = setTimeout(
        () => {
          this.transitionComplete = true;
        },
        this.showCompleteAnimation ? 2500 : 0,
      );
    }

    return x` ${this.reverseTransitionComplete
      ? A
      : x`<div class="cover" aria-hidden="true" ?hidden="${beginCompletionAnimation}"></div>
          <div class="refi-curtain" aria-hidden="true">
            ${curtainAnimation}
          </div>
          ${this.showCompleteAnimation || !beginCompletionAnimation
            ? x` <dialog id="modal" ?hidden="${this.loading}" tabindex="-1" aria-modal="true" aria-labelledby="Transition Page">
                <section>
                  <div id="content" aria-hidden="true" ?hidden="${this.loading}">
                    <div id="animation-slot">
                      ${beginCompletionAnimation ? x`${completeAnimation}` : x`<slot name="animation"></slot>`}
                    </div>
                    <h1 id="animation-text" tabindex="0">
                      <slot name="${beginCompletionAnimation ? 'animation-text-complete' : 'animation-text'}"></slot>
                    </h1>
                  </div>
                </section>
              </dialog>`
            : A}`}`;
  }

  firstUpdated() {
    // start the curtain animation, if we are not skipping it, play the curtain animation
    if (!this.skipCurtainOpenAnimation) {
      const curtainElement = this.shadowRoot.getElementById('curtainAnimate');
      curtainElement.addEventListener('endEvent', () => {
        this.loading = false;
      });
      curtainElement.beginElement();
      this.shadowRoot.getElementById('modal').showModal();
      setTimeout(() => {
        this.shadowRoot.getElementById('animation-text').focus();
      }, 1000);
    } else {
      // else immediately show the modal and content
      // TODO MAKE IT JUMP TO BEING DOWN, not sure how atm
      this.loading = false;
      this.shadowRoot.getElementById('modal').showModal();
      this.shadowRoot.getElementById('animation-text').focus();
    }
  }

  resetAnimationTimeout() {
    this.animationTimeout = setTimeout(() => {
      this.minimumAnimationLengthComplete = true;
    }, this.minimumAnimationLengthMs);
  }
}

export { RefiTransitionPage };
